import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import styles from './ErrorTestingPage.module.scss';
import { api } from '@/api/REST/axiosClient';
import type { ApiError } from '@/api/REST/errors';
import {
  extractFieldErrors,
  formatErrorsForDisplay,
  getGeneralErrors,
  isApiError,
} from '@/api/REST/errors';
import { PanelContent } from '@/components/layouts/PanelContent';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { PageHeader } from '@/components/ui/PageHeader';
import { useNotification } from '@/hooks/useNotification';

export const ErrorTestingPage = () => {
  const { t, i18n } = useTranslation('errors');
  const { showError, showSuccess } = useNotification();
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Test: Simulate backend error with single validation error
  const singleFieldErrorMutation = useMutation({
    mutationFn: async () => {
      throw {
        status: 400,
        errors: [
          {
            labelKey: 'ProjectNameTooLong',
            message: 'Project name must not exceed 255 characters.',
            field: 'Name',
            params: {
              MinLength: '0',
              MaxLength: '255',
              TotalLength: '256',
            },
          },
        ],
        traceId: 'TEST-TRACE-001',
      } as ApiError;
    },
    onError: (error) => {
      if (isApiError(error)) {
        setLastError(error);
        const errorDisplay = formatErrorsForDisplay(error, {
          t,
          i18n,
          namespace: 'errors',
        });
        showError({ message: errorDisplay as string });
      }
    },
  });

  // Test: Simulate backend error with multiple validation errors
  const multipleFieldErrorsMutation = useMutation({
    mutationFn: async () => {
      throw {
        status: 400,
        errors: [
          {
            labelKey: 'Required',
            message: 'Name is required',
            field: 'Name',
          },
          {
            labelKey: 'Invalid',
            message: 'Invalid email address',
            field: 'Email',
          },
          {
            labelKey: 'MustBePositive',
            message: 'Amount must be positive',
            field: 'Amount',
          },
        ],
        traceId: 'TEST-TRACE-002',
      } as ApiError;
    },
    onError: (error) => {
      if (isApiError(error)) {
        setLastError(error);
        const errorDisplay = formatErrorsForDisplay(error, {
          t,
          i18n,
          namespace: 'errors',
        });
        if (Array.isArray(errorDisplay)) {
          showError({
            message: (
              <>
                {errorDisplay.map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
              </>
            ),
            persist: true,
          });
        }
      }
    },
  });

  // Test: Simulate general error without field
  const generalErrorMutation = useMutation({
    mutationFn: async () => {
      throw {
        status: 500,
        errors: [
          {
            labelKey: 'ServerError',
            message: 'Server error. Please try again later.',
          },
        ],
        traceId: 'TEST-TRACE-003',
      } as ApiError;
    },
    onError: (error) => {
      if (isApiError(error)) {
        setLastError(error);
        const generalErrors = getGeneralErrors(error, {
          t,
          i18n,
          namespace: 'errors',
        });
        showError({
          message:
            generalErrors[0] ??
            t('common.ServerError', {
              ns: 'errors',
              defaultValue: 'Server error. Please try again later.',
            }),
        });
      }
    },
  });

  // Test: Simulate network error
  const networkErrorMutation = useMutation({
    mutationFn: async () => {
      throw new Error('Network error. Please check your connection.');
    },
    onError: (error) => {
      showError({
        message:
          error instanceof Error
            ? error.message
            : t('common.NetworkError', {
                ns: 'errors',
                defaultValue: 'Network error. Please check your connection.',
              }),
      });
    },
  });

  // Test: Real HTTP request through the api interceptor
  const httpInterceptorMutation = useMutation({
    mutationFn: async () => {
      await api.get('/api/test/non-existent-endpoint');
    },
    onError: (error) => {
      if (isApiError(error)) {
        setLastError(error);
        showError({
          message:
            error.errors[0]?.message ??
            t('common.UnknownError', {
              ns: 'errors',
              defaultValue: 'Unknown error',
            }),
        });
      }
      // { _handledByToast: true } errors are already handled globally by useHttpErrorHandler
    },
  });

  // Test: Global HTTP error handlers via event dispatch
  const triggerUnauthorized = () => {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  };

  const triggerForbidden = () => {
    window.dispatchEvent(new CustomEvent('api:forbidden'));
  };

  const triggerServerError = () => {
    window.dispatchEvent(
      new CustomEvent('api:server-error', { detail: { status: 500 } }),
    );
  };

  // Test: Trigger React error boundary
  const [shouldThrowError, setShouldThrowError] = useState(false);
  if (shouldThrowError) {
    throw new Error('Test React Error Boundary - Intentional crash!');
  }

  // Test: Query error
  const errorQuery = useQuery({
    queryKey: ['test-error'],
    queryFn: async () => {
      throw {
        status: 404,
        errors: [
          {
            labelKey: 'NotFound',
            message: 'The requested resource was not found.',
          },
        ],
        traceId: 'TEST-TRACE-004',
      } as ApiError;
    },
    enabled: false,
    retry: false,
  });

  return (
    <PanelContent>
      <PageHeader
        title="Error Testing Page"
        subtitle="This page allows you to test various error scenarios and verify that error handling is working correctly."
      />

      <Stack gap={3} className={styles.ErrorTests}>
        {/* Single Field Error */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                1. Single Field Error
              </Typography>
              <Typography color="text.secondary">
                Simulates a validation error on a single field (e.g.,
                &quot;Project name too long&quot;)
              </Typography>
              <Button
                variant="outlined"
                onClick={() => singleFieldErrorMutation.mutate()}
                disabled={singleFieldErrorMutation.isPending}
              >
                Trigger Single Field Error
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Multiple Field Errors */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                2. Multiple Field Errors
              </Typography>
              <Typography color="text.secondary">
                Simulates multiple validation errors on different fields (Name,
                Email, Amount)
              </Typography>
              <Button
                variant="outlined"
                onClick={() => multipleFieldErrorsMutation.mutate()}
                disabled={multipleFieldErrorsMutation.isPending}
              >
                Trigger Multiple Field Errors
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* General Error */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                3. General Error (No Field)
              </Typography>
              <Typography color="text.secondary">
                Simulates a general server error not associated with any
                specific field
              </Typography>
              <Button
                variant="outlined"
                onClick={() => generalErrorMutation.mutate()}
                disabled={generalErrorMutation.isPending}
              >
                Trigger General Error
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Network Error */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                4. Network Error
              </Typography>
              <Typography color="text.secondary">
                Simulates a generic JavaScript Error (like network failure)
              </Typography>
              <Button
                variant="outlined"
                onClick={() => networkErrorMutation.mutate()}
                disabled={networkErrorMutation.isPending}
              >
                Trigger Network Error
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* HTTP Interceptor */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                5. HTTP Interceptor (real request)
              </Typography>
              <Typography color="text.secondary">
                Makes a real request through the <code>api</code> instance to a
                non-existent endpoint. The interceptor normalizes the response
                into an ApiError.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => httpInterceptorMutation.mutate()}
                disabled={httpInterceptorMutation.isPending}
              >
                Trigger HTTP Interceptor
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Global HTTP Error Handlers */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                6. Global HTTP Error Handlers
              </Typography>
              <Typography color="text.secondary">
                Dispatches the custom events that the interceptor fires for
                401/403/5xx responses. Tests that{' '}
                <code>useHttpErrorHandler</code> shows the correct toast (and
                redirects for 401).
              </Typography>
              <Alert severity="warning">
                401 will redirect you to the login page.
              </Alert>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={triggerUnauthorized}
                >
                  401 Unauthorized
                </Button>
                <Button variant="outlined" onClick={triggerForbidden}>
                  403 Forbidden
                </Button>
                <Button variant="outlined" onClick={triggerServerError}>
                  5xx Server Error
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Query Error */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                7. React Query Error
              </Typography>
              <Typography color="text.secondary">
                Simulates an error in a React Query hook (useQuery)
              </Typography>
              <Button
                variant="outlined"
                onClick={() => errorQuery.refetch()}
                disabled={errorQuery.isFetching}
              >
                Trigger Query Error
              </Button>
              {errorQuery.isError && <ApiErrorAlert error={errorQuery.error} />}
            </Stack>
          </CardContent>
        </Card>

        {/* React Error Boundary */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                8. React Error Boundary
              </Typography>
              <Typography color="text.secondary">
                Triggers a React rendering error to test the ErrorBoundary
                component
              </Typography>
              <Alert severity="warning">
                Warning: This will crash the component tree! The ErrorBoundary
                will catch it and show a fallback UI.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShouldThrowError(true)}
              >
                Trigger React Error Boundary
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Divider />

        {/* Last Error Display */}
        {lastError && (
          <Card>
            <CardContent>
              <Stack gap={2}>
                <Typography variant="h3" component="h2">
                  Last Error Details
                </Typography>
                <Alert severity="error">
                  <Stack gap={1}>
                    <Typography>
                      <strong>Status:</strong> {lastError.status}
                    </Typography>
                    {lastError.traceId && (
                      <Typography>
                        <strong>Trace ID:</strong> {lastError.traceId}
                      </Typography>
                    )}
                    <Typography>
                      <strong>Errors:</strong>
                    </Typography>
                    {lastError.errors.map((err, idx) => (
                      <Box key={idx} pl={2}>
                        <Typography>
                          • {err.field && `[${err.field}] `}
                          {err.message}
                        </Typography>
                        {err.labelKey && (
                          <Typography variant="label" color="text.secondary">
                            Label Key: {err.labelKey}
                          </Typography>
                        )}
                      </Box>
                    ))}
                    <Divider />
                    <Typography>
                      <strong>Field Errors:</strong>
                    </Typography>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(
                        extractFieldErrors(lastError, {
                          t,
                          i18n,
                          namespace: 'errors',
                        }),
                        null,
                        2,
                      )}
                    </pre>
                  </Stack>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Test Success Notification */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h3" component="h2">
                Test Success Notification
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  showSuccess({ message: 'This is a success notification!' })
                }
              >
                Show Success
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </PanelContent>
  );
};
